<template>
  <div class="dbm">
    <!-- ── Hero ── -->
    <div class="dbm-hero">
      <div class="dbm-hero__bg"/>
      <div class="dbm-hero__content">
        <div class="dbm-hero__left">
          <div class="dbm-hero__icon-wrap"><i class="fa-solid fa-chart-column"/></div>
          <div>
            <h4 class="dbm-hero__title">{{ $t('contractStats.monthlyRevenue') }}</h4>
            <p class="dbm-hero__sub">{{ $t('contractStats.monthlyOverview') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Filters ── -->
    <div class="dbm-filter-card">
      <div class="dbm-filter-card__date">
        <calendar-monthly-bar v-model="selectedMonths" @input="onMonthSelected" :max-options-size="6" :future-options-size="3"/>
      </div>
      <b-row class="align-items-end">
        <b-col cols="12" md="4">
          <label class="dbm-filter-label">{{ $t('contractStats.selectBuilding') }}</label>
          <building-select-box only-active v-model="buildings" multiple clearable @input="refresh"/>
        </b-col>
        <b-col cols="12" md="7">
          <label class="dbm-filter-label">{{ $t('contractStats.selectProperty') }}</label>
          <real-estate-picker multiple v-model="properties" :buildings="buildings" clearable @input="refresh"/>
        </b-col>
        <b-col cols="12" md="1">
          <b-button :disabled="reportDisabled || loading.requestReport" block variant="primary" class="dbm-dl-btn" @click="requestReport"
                    v-b-tooltip.hover="$t('contractStats.downloadReport')">
            <b-spinner small type="grow" v-if="loading.requestReport"/>
            <i v-else class="fa-solid fa-file-arrow-down"/>
          </b-button>
        </b-col>
      </b-row>
    </div>

    <!-- Loading -->
    <div v-if="loading.stats" class="dbm-loading">
      <b-spinner variant="primary" style="width:2.5rem;height:2.5rem"/>
    </div>

    <template v-else>
      <!-- ══ Income cards ══ -->
      <h6 class="dbm-section"><i class="fa-solid fa-arrow-trend-up mr-50 text-success"/> {{ $t('contractStats.income') }}</h6>
      <div class="dbm-kpi-grid">
        <div class="dbm-kpi-3d" style="--bg-from:#28c76f;--bg-to:#6ee7b7;--accent:#28c76f">
          <div class="dbm-kpi-3d__glow"/>
          <div class="dbm-kpi-3d__icon"><i class="fa-solid fa-file-contract"/></div>
          <div class="dbm-kpi-3d__value">{{ stats.totalOfContracts || 0 }}</div>
          <div class="dbm-kpi-3d__label">{{ $t('contractStats.totalOfContracts') }}</div>
          <div v-if="platforms.length" class="dbm-kpi-3d__platforms">
            <platform-logo v-for="p in platforms" :key="p.name" :platform="p.name" size="14px" class="mr-50">
              <span class="dbm-kpi-3d__pcnt">{{ p.count }}</span>
            </platform-logo>
          </div>
        </div>
        <div class="dbm-kpi-3d" style="--bg-from:#0ea5e9;--bg-to:#7dd3fc;--accent:#0ea5e9">
          <div class="dbm-kpi-3d__glow"/>
          <div class="dbm-kpi-3d__icon"><i class="fas fa-coins"/></div>
          <div class="dbm-kpi-3d__value">{{ fmtNum(stats.totalIncome) }} <small>€</small></div>
          <div class="dbm-kpi-3d__label">{{ $t('contractStats.totalIncome') }}</div>
        </div>
        <div class="dbm-kpi-3d" style="--bg-from:#7367f0;--bg-to:#a78bfa;--accent:#7367f0">
          <div class="dbm-kpi-3d__glow"/>
          <div class="dbm-kpi-3d__icon"><i class="fa-solid fa-cash-register"/></div>
          <div class="dbm-kpi-3d__value">{{ fmtNum(stats.totalNetIncome) }} <small>€</small></div>
          <div class="dbm-kpi-3d__label">{{ $t('contractStats.totalNetIncome') }}</div>
          <div class="dbm-kpi-3d__sub-white">{{ $t('contractStats.withoutRent') }}: {{ (stats.totalNetIncomeWithoutRent || 0).toFixed(2) }} €</div>
        </div>
      </div>

      <!-- ══ Expense cards ══ -->
      <h6 class="dbm-section"><i class="fa-solid fa-arrow-trend-down mr-50 text-warning"/> {{ $t('contractStats.expenses') }}</h6>
      <div class="dbm-kpi-grid">
        <div class="dbm-kpi-3d" style="--bg-from:#ff9f43;--bg-to:#fdba74;--accent:#ff9f43">
          <div class="dbm-kpi-3d__glow"/>
          <div class="dbm-kpi-3d__icon"><i class="fa-solid fa-broom"/></div>
          <div class="dbm-kpi-3d__value">{{ fmtAbs(stats.totalExpensesWithoutRent) }} <small>€</small></div>
          <div class="dbm-kpi-3d__label">{{ $t('contractStats.totalExpenses') }}</div>
        </div>
        <div class="dbm-kpi-3d" style="--bg-from:#ea5455;--bg-to:#fca5a5;--accent:#ea5455">
          <div class="dbm-kpi-3d__glow"/>
          <div class="dbm-kpi-3d__icon"><i class="fa-solid fa-chart-pie"/></div>
          <div class="dbm-kpi-3d__value">{{ fmtAbs(stats.totalExternalPlatformCommission + stats.totalExternalPlatformTransactionFee) }} <small>€</small></div>
          <div class="dbm-kpi-3d__label">{{ $t('contractStats.commissions') }}</div>
          <div class="dbm-kpi-3d__sub-white">{{ $t('contractStats.transactionFees') }}: {{ fmtAbs(stats.totalExternalPlatformTransactionFee) }} €</div>
        </div>
        <div class="dbm-kpi-3d" style="--bg-from:#f59e0b;--bg-to:#fde68a;--accent:#f59e0b">
          <div class="dbm-kpi-3d__glow"/>
          <div class="dbm-kpi-3d__icon"><i class="fa-solid fa-people-roof"/></div>
          <div class="dbm-kpi-3d__value">{{ fmtAbs(stats.totalManagementFee) }} <small>€</small></div>
          <div class="dbm-kpi-3d__label">{{ $t('contractStats.managementFee') }}</div>
        </div>
      </div>

      <!-- ══ Quick stats ══ -->
      <div class="dbm-quick">
        <div v-for="s in quickStats" :key="s.label" class="dbm-quick__pill" :style="{'--pill': s.pillColor}">
          <span class="dbm-quick__label">{{ s.label }}</span>
          <span class="dbm-quick__val" :class="s.cls">{{ s.value }}</span>
        </div>
      </div>

      <!-- ══ Charts ══ -->
      <b-row class="mt-50">
        <!-- Donut -->
        <b-col cols="12" lg="4">
          <div class="dbm-chart-card">
            <div class="dbm-chart-card__head">
              <h6>{{ $t('contractStats.platformDistribution') }}</h6>
              <b-form-checkbox inline switch size="sm" v-model="showPlatformsIncomes" class="ml-auto mb-0">{{ $t('contractStats.netIncome') }}</b-form-checkbox>
            </div>
            <div class="dbm-chart-card__body">
              <vue-apex-charts v-if="donutSeries.length" ref="donut" type="donut" height="310" :options="donutOpts" :series="donutSeries"/>
              <div v-else class="dbm-no-data">{{ $t('nodatafound') }}</div>
            </div>
          </div>
        </b-col>
        <!-- Radial -->
        <b-col cols="12" lg="4">
          <div class="dbm-chart-card">
            <div class="dbm-chart-card__head"><h6>{{ $t('contractStats.occupancyRate') }}</h6></div>
            <div class="dbm-chart-card__body">
              <vue-apex-charts v-if="radialSeries.length" ref="radial" type="radialBar" height="290" :options="radialOpts" :series="radialSeries"/>
              <div v-else class="dbm-no-data">{{ $t('nodatafound') }}</div>
              <div v-if="capacityData.totalOfProperties" class="dbm-cap-legend">
                <span v-b-tooltip :title="$t('contractStats.properties')"><i class="fa-solid fa-house"/> {{ capacityData.totalOfProperties }}</span>
                <span v-b-tooltip :title="$t('contractStats.nightsBooked')"><i class="fa-solid fa-moon"/> {{ capacityData.totalOfNights }}</span>
                <span v-b-tooltip :title="$t('contractStats.totalOfContracts')"><i class="fa-solid fa-file-contract"/> {{ capacityData.totalOfContracts }}</span>
              </div>
            </div>
          </div>
        </b-col>
        <!-- Top properties (horizontal bar with gradient) -->
        <b-col cols="12" lg="4">
          <div class="dbm-chart-card">
            <div class="dbm-chart-card__head"><h6>{{ $t('contractStats.topPropertiesByIncomes') }}</h6></div>
            <div class="dbm-chart-card__body">
              <vue-apex-charts v-if="topPropSeries.length && topPropSeries[0].data.length" ref="topbar" type="bar" height="310" :options="topPropOpts" :series="topPropSeries"/>
              <div v-else class="dbm-no-data">{{ $t('nodatafound') }}</div>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- ══ Revenue breakdown (treemap) ══ -->
      <div class="dbm-chart-card mt-1" v-if="treemapSeries.length && treemapSeries[0].data.length">
        <div class="dbm-chart-card__head"><h6>{{ $t('contractStats.revenueBreakdown') }}</h6></div>
        <div class="dbm-chart-card__body">
          <vue-apex-charts ref="treemap" type="treemap" height="340" :options="treemapOpts" :series="treemapSeries"/>
        </div>
      </div>

      <!-- ══ Expenses table ══ -->
      <div class="mt-1">
        <contract-monthly-expenses-table :loading="loading.stats" :stats="stats"/>
      </div>
    </template>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import {mapActions, mapGetters} from 'vuex'
import CalendarMonthlyBar from '@/components/widgets/calendar/CalendarMonthlyBar.vue'
import BuildingSelectBox from '@/components/components/picker/buildingSelectBox.vue'
import RealEstatePicker from '@/components/components/picker/realEstatesPicker.vue'
import PlatformLogo from '@/components/widgets/PlatformLogo.vue'
import ContractMonthlyExpensesTable from '@/components/panels/dashboard/contractMonthlyExpensesTable.vue'

const emptyStats = () => ({
  totalOfContracts: 0, totalIncome: 0, totalNetIncome: 0, totalNetIncomeWithoutRent: 0,
  totalExpensesWithoutRent: 0, totalManagementFee: 0,
  totalExternalPlatformCommission: 0, totalExternalPlatformTransactionFee: 0,
  realEstatesCalculations: {}, contracts: [],
})

export default {
  name: 'contractRentMonthlyDashboard',
  components: {VueApexCharts, CalendarMonthlyBar, BuildingSelectBox, RealEstatePicker, PlatformLogo, ContractMonthlyExpensesTable},
  data: () => ({
    selectedMonths: {start: null, end: null}, buildings: [], properties: [],
    showPlatformsIncomes: false, stats: emptyStats(),
    loading: {requestReport: false, stats: false},
  }),
  created() { this.$root.$on('crm::branch-updated', this.refresh) },
  beforeDestroy() { this.$root.$off('crm::branch-updated', this.refresh) },
  computed: {
    ...mapGetters('appConfig', ['isDarkMode']),
    platforms() {
      const r = {}
      for (const c of (this.stats.contracts || [])) {
        const n = c?.bnbStyleRent?.platform; if (!n) continue
        if (!r[n]) r[n] = {name: n, platform: n, paymentFee: 0, netPayment: 0, totalPayment: 0, count: 0}
        r[n].paymentFee += c.bnbStyleRent.totalPaymentFee
        r[n].netPayment = parseFloat((r[n].netPayment + c.bnbStyleRent.netPaymentIncome).toFixed(2))
        r[n].totalPayment += c.bnbStyleRent.totalPayment; r[n].count++
      }
      return Object.values(r)
    },
    propertiesIncomes() {
      return Object.values(this.stats.realEstatesCalculations || {})
        .map(i => ({fullAddress: i.fullAddress, totalIncome: i.totalIncome, totalOfContracts: i.totalOfContracts,
          totalNetIncome: i.totalNetIncome - i.managementFee, managementFee: i.managementFee, rent: i.expenses.rent,
          totalExternalPlatformCommission: i.externalPlatformCommission + i.externalPlatformTransactionFee, totalExpensesWithoutRent: i.expenses.totalExpensesWithoutRent}))
        .filter(i => i.managementFee > 0).sort((a, b) => b.totalNetIncome - a.totalNetIncome).slice(0, 12)
    },
    capacityData() {
      if (!this.stats.months?.length) return {totalDays: 0, totalOfProperties: 0, totalOfContracts: 0, totalOfNights: 0}
      const totalDays = this.stats.months.reduce((a, m) => a + this.$moment(m, 'MM-YYYY').daysInMonth(), 0)
      const totalOfProperties = Object.keys(this.stats.realEstatesCalculations || {}).length
      const totalOfNights = Object.values(this.stats.realEstatesCalculations || {}).reduce((a, c) => a + c.totalOfNights, 0)
      return {totalDays, totalOfProperties, totalOfContracts: this.stats.totalOfContracts, totalOfNights}
    },
    reportDisabled() { return !Array.isArray(this.buildings) || this.buildings.length !== 1 },

    quickStats() {
      const props = Object.keys(this.stats.realEstatesCalculations || {}).length
      const avg = this.stats.totalOfContracts ? ((this.stats.totalIncome || 0) / this.stats.totalOfContracts).toFixed(2) : '0.00'
      const margin = this.stats.totalIncome ? (((this.stats.totalNetIncome || 0) / this.stats.totalIncome) * 100).toFixed(1) : '0.0'
      const cd = this.capacityData; const td = (cd.totalDays || 0) * cd.totalOfProperties
      const cap = td ? (((cd.totalOfNights || 0) / td) * 100).toFixed(1) : '0.0'
      return [
        {label: this.$t('contractStats.totalProperties'), value: props, cls: '', pillColor: '#7367f0'},
        {label: this.$t('contractStats.avgPerContract'), value: `${avg} €`, cls: '', pillColor: '#0ea5e9'},
        {label: this.$t('contractStats.profitMargin'), value: `${margin}%`, cls: parseFloat(margin) >= 0 ? 'text-success' : 'text-danger', pillColor: '#28c76f'},
        {label: this.$t('contractStats.capacityUsage'), value: `${cap}%`, cls: 'text-primary', pillColor: '#ff9f43'},
      ]
    },

    /* Donut */
    donutLabels() { return this.platforms.map(p => p.name) },
    donutSeries() { return this.platforms.map(p => this.showPlatformsIncomes ? p.netPayment : p.count) },
    donutOpts() {
      return {
        chart: {type: 'donut', foreColor: this.isDarkMode ? '#ccc' : '#555', dropShadow: {enabled: true, top: 4, blur: 8, opacity: .1}},
        labels: this.donutLabels.map((l, i) => `${l} (${this.showPlatformsIncomes ? this.platforms[i].netPayment + ' €' : this.platforms[i].count})`),
        colors: this.donutLabels.map(p => this.pColor(p)),
        stroke: {width: 3, colors: [this.isDarkMode ? '#283046' : '#fff']},
        legend: {position: 'bottom', fontFamily: 'Montserrat', fontSize: '12px', markers: {width: 10, height: 10, radius: 4}},
        plotOptions: {pie: {donut: {size: '68%', labels: {show: true, name: {fontSize: '13px', fontWeight: 700}, value: {fontSize: '22px', fontWeight: 900, offsetY: 4}, total: {show: true, label: this.$t('contractStats.allPlatforms'), fontSize: '11px'}}}}},
        dataLabels: {enabled: false},
      }
    },
    /* Radial */
    radialSeries() {
      const cd = this.capacityData; const td = (cd.totalDays || 0) * cd.totalOfProperties
      if (!td) return []; return [parseFloat(((cd.totalOfNights / td) * 100).toFixed(1))]
    },
    radialOpts() {
      return {
        chart: {type: 'radialBar', dropShadow: {enabled: true, top: 3, blur: 8, opacity: .08}},
        plotOptions: {radialBar: {hollow: {size: '62%'}, track: {background: this.isDarkMode ? '#3b4253' : '#f1f5f9', strokeWidth: '100%'},
          dataLabels: {name: {fontSize: '13px', color: this.isDarkMode ? '#b4b7bd' : '#64748b', offsetY: -8}, value: {fontSize: '30px', fontWeight: 900, color: this.isDarkMode ? '#d0d2d6' : '#1e293b'}}}},
        stroke: {lineCap: 'round'}, colors: ['#7367f0'],
        labels: [this.$t('contractStats.occupancyRate')],
      }
    },
    /* Top properties */
    topPropOpts() {
      const labels = this.propertiesIncomes.slice(0, 8).map(p => p.fullAddress.length > 18 ? p.fullAddress.substring(0, 18) + '…' : p.fullAddress)
      return {
        chart: {type: 'bar', foreColor: this.isDarkMode ? '#ccc' : '#555', toolbar: {show: false}},
        plotOptions: {bar: {horizontal: true, borderRadius: 6, barHeight: '55%'}},
        dataLabels: {enabled: true, formatter: v => `${v.toFixed(0)} €`, style: {fontSize: '11px', fontWeight: 700}},
        xaxis: {categories: labels, labels: {formatter: v => `${v.toFixed(0)} €`}},
        colors: ['#7367f0'], fill: {type: 'gradient', gradient: {shade: 'light', type: 'horizontal', shadeIntensity: .2, gradientToColors: ['#ce9ffc'], stops: [0, 100]}},
        grid: {borderColor: this.isDarkMode ? 'rgba(255,255,255,.06)' : 'rgba(0,0,0,.06)', strokeDashArray: 5},
        tooltip: {y: {formatter: v => `${v.toFixed(2)} €`}},
      }
    },
    topPropSeries() { return [{name: this.$t('contractStats.netIncome'), data: this.propertiesIncomes.slice(0, 8).map(p => parseFloat(p.totalNetIncome.toFixed(2)))}] },

    /* Treemap (replaces stacked bar) */
    treemapSeries() {
      return [{data: this.propertiesIncomes.map(p => ({x: p.fullAddress.length > 22 ? p.fullAddress.substring(0, 22) + '…' : p.fullAddress, y: parseFloat(p.totalNetIncome.toFixed(2))}))}]
    },
    treemapOpts() {
      return {
        chart: {type: 'treemap', foreColor: this.isDarkMode ? '#ccc' : '#555', toolbar: {show: false}},
        plotOptions: {treemap: {distributed: true, enableShades: false}},
        colors: ['#7367f0', '#28c76f', '#0ea5e9', '#ff9f43', '#ea5455', '#c2aa8e', '#f59e0b', '#6366f1', '#14b8a6', '#f43f5e', '#8b5cf6', '#06b6d4'],
        dataLabels: {enabled: true, style: {fontSize: '12px', fontWeight: 700}, formatter: (t, o) => `${t}\n${o.value.toFixed(0)} €`},
        tooltip: {y: {formatter: v => `${v.toFixed(2)} €`}},
      }
    },
  },
  methods: {
    ...mapActions('realEstateContractsStats', ['getExpensesStats', 'getExpensesStatsReport']),
    pColor(p) { return {'Treasure Home': '#c2aa8e', Airbnb: '#FF4560', Booking: '#3F51B5', Expedia: '#FF9800', Agoda: '#4CAF50', Vrbo: '#96a6b1'}[p] || '#00BCD4' },
    fmtNum(v) { return this.$options.filters.numberFormatter(v || 0) },
    fmtAbs(v) { return this.$options.filters.numberFormatter(Math.abs(v || 0)) },
    refresh() {
      this.loading.stats = true
      this.getExpensesStats({...this.selectedMonths, properties: this.properties, buildings: this.buildings})
        .then(data => { this.stats = {...emptyStats(), ...data.stats} }).finally(() => { this.loading.stats = false })
    },
    requestReport() {
      this.loading.requestReport = true
      this.getExpensesStatsReport({...this.selectedMonths, properties: this.properties, buildings: this.buildings})
        .then(r => { const l = document.createElement('a'); l.href = window.URL.createObjectURL(new Blob([r.data])); l.download = r.headers['content-disposition'].split('filename=')[1]; l.click() })
        .catch(e => console.error('Report error', e)).finally(() => { this.loading.requestReport = false })
    },
    onMonthSelected(m) { this.selectedMonths = m; this.refresh() },
  },
}
</script>

<style lang="scss" scoped>
/* ── Hero ── */
.dbm-hero { position: relative; border-radius: 18px; overflow: hidden; margin-bottom: 18px; }
.dbm-hero__bg { position: absolute; inset: 0; background: linear-gradient(135deg, #059669 0%, #34d399 60%, #6ee7b7 100%); z-index: 0; }
.dbm-hero__content { position: relative; z-index: 1; padding: 22px 28px; }
.dbm-hero__left { display: flex; align-items: center; gap: 16px; }
.dbm-hero__icon-wrap {
  width: 52px; height: 52px; border-radius: 16px; display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,.22); backdrop-filter: blur(6px); font-size: 1.3rem; color: #fff;
  box-shadow: 0 4px 14px rgba(0,0,0,.12);
}
.dbm-hero__title { font-weight: 800; font-size: 1.2rem; margin: 0; color: #fff; }
.dbm-hero__sub { font-size: .82rem; color: rgba(255,255,255,.75); margin: 0; }

/* ── Filter card ── */
.dbm-filter-card {
  background: #fff; border-radius: 16px; padding: 16px 22px 8px; margin-bottom: 20px;
  box-shadow: 0 4px 20px rgba(0,0,0,.05); border: 1px solid rgba(0,0,0,.04);
}
.dbm-filter-card__date { margin-bottom: 8px; }
.dbm-filter-label { font-size: .72rem; font-weight: 700; text-transform: uppercase; color: #64748b; letter-spacing: .04em; margin-bottom: 4px; display: block; }
.dbm-dl-btn { border-radius: 10px; height: 38px; font-size: 1rem; margin-top: 20px; }

/* ── Section ── */
.dbm-section { font-size: .78rem; font-weight: 800; text-transform: uppercase; letter-spacing: .06em; color: #475569; margin: 22px 0 12px 4px; }

/* ── 3D KPI cards ── */
.dbm-kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; margin-bottom: 8px; align-items: stretch; }
@media (max-width: 992px) { .dbm-kpi-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 576px) { .dbm-kpi-grid { grid-template-columns: 1fr; } }

.dbm-kpi-3d {
  position: relative; border-radius: 16px; padding: 22px 20px 18px; text-align: center; overflow: hidden;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  background: linear-gradient(145deg, var(--bg-from), var(--bg-to)); color: #fff;
  box-shadow: 0 8px 28px -4px color-mix(in srgb, var(--accent) 35%, transparent), 0 2px 6px rgba(0,0,0,.06);
  transition: transform .2s, box-shadow .25s;
}
.dbm-kpi-3d:hover { transform: translateY(-4px) scale(1.015); box-shadow: 0 14px 36px -4px color-mix(in srgb, var(--accent) 45%, transparent), 0 4px 10px rgba(0,0,0,.08); }
.dbm-kpi-3d__glow {
  position: absolute; top: -30px; right: -30px; width: 100px; height: 100px; border-radius: 50%;
  background: rgba(255,255,255,.15); filter: blur(28px); pointer-events: none;
}
.dbm-kpi-3d__icon { font-size: 1.6rem; margin-bottom: 8px; opacity: .85; text-shadow: 0 2px 6px rgba(0,0,0,.15); }
.dbm-kpi-3d__value { font-size: 2.2rem; font-weight: 900; line-height: 1; text-shadow: 0 2px 8px rgba(0,0,0,.12); }
.dbm-kpi-3d__value small { font-size: .9rem; opacity: .7; }
.dbm-kpi-3d__label { font-size: .72rem; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; opacity: .85; margin-top: 4px; }
.dbm-kpi-3d__platforms { display: flex; justify-content: center; flex-wrap: wrap; gap: 8px; margin-top: auto; padding-top: 10px; border-top: 1px solid rgba(255,255,255,.2); width: 100%; }
.dbm-kpi-3d__pcnt { font-weight: 800; font-size: .8rem; color: #fff; margin-left: 2px; }
.dbm-kpi-3d__sub-white { font-size: .73rem; font-weight: 600; opacity: .8; margin-top: auto; padding-top: 8px; border-top: 1px solid rgba(255,255,255,.18); width: 100%; }

/* ── Quick stat pills ── */
.dbm-quick { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin: 18px 0; align-items: stretch; }
@media (max-width: 768px) { .dbm-quick { grid-template-columns: repeat(2, 1fr); } }
.dbm-quick__pill {
  background: #fff; border-radius: 12px; padding: 14px 18px; display: flex; justify-content: space-between; align-items: center;
  box-shadow: 0 3px 14px rgba(0,0,0,.04); border-left: 4px solid var(--pill); transition: transform .15s;
}
.dbm-quick__pill:hover { transform: translateX(2px); }
.dbm-quick__label { font-size: .76rem; font-weight: 600; color: #64748b; }
.dbm-quick__val { font-size: .95rem; font-weight: 800; }

/* ── Chart cards ── */
.dbm-chart-card {
  background: #fff; border-radius: 16px; overflow: hidden; margin-bottom: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,.05); border: 1px solid rgba(0,0,0,.04);
}
.dbm-chart-card__head {
  padding: 16px 22px 12px; border-bottom: 1px solid rgba(0,0,0,.05);
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 4px;
}
.dbm-chart-card__head h6 { font-weight: 800; font-size: .92rem; margin: 0; color: #1e293b; }
.dbm-chart-card__body { padding: 14px 18px; min-height: 320px; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.dbm-no-data { color: #94a3b8; font-size: .85rem; }

.dbm-cap-legend { display: flex; gap: 18px; justify-content: center; padding: 8px 0 2px; font-size: .85rem; font-weight: 700; color: #475569; }
.dbm-cap-legend i { margin-right: 4px; opacity: .6; }

/* ── Loading ── */
.dbm-loading { display: flex; align-items: center; justify-content: center; min-height: 350px; }
</style>

<style lang="scss">
.dark-layout .dbm-hero__bg { background: linear-gradient(135deg, #065f46 0%, #059669 60%, #34d399 100%); }
.dark-layout .dbm-filter-card, .dark-layout .dbm-quick__pill, .dark-layout .dbm-chart-card {
  background: #283046 !important; border-color: rgba(255,255,255,.06) !important;
}
.dark-layout .dbm-chart-card__head h6 { color: #d0d2d6 !important; }
.dark-layout .dbm-chart-card__head { border-color: rgba(255,255,255,.06) !important; }
.dark-layout .dbm-quick__label, .dark-layout .dbm-section, .dark-layout .dbm-filter-label { color: #b4b7bd !important; }
.dark-layout .dbm-cap-legend { color: #b4b7bd; }
</style>
